/**
 * Mints the service-auth JWT the calls worker expects, for either identity kind:
 *
 *   - Real atproto user: their PDS signs it via com.atproto.server.getServiceAuth
 *     (reuses the app's existing getServiceAuthToken helper).
 *   - Guest: we hand-assemble the compact JWT and sign it with the local
 *     did:key keypair (guestIdentity.ts).
 *
 * Both produce the SAME token shape (iss / aud / lxm / exp), and the worker
 * verifies both on one path. Tokens are short-lived; callers re-mint as needed
 * (on reconnect / 401).
 */

import {type AtpAgent} from '@atproto/api'

import {CALLS_LXM, CALLS_SERVICE_DID} from '#/lib/constants'
import {getServiceAuthToken} from '#/lib/media/video/upload.shared'
import {type CallIdentity, getGuestIdentity} from './guestIdentity'

const TOKEN_TTL_SECONDS = 60 * 10 // 10 minutes

export interface CallSession {
  /** The caller's DID (real or guest). Identity used everywhere downstream. */
  did: string
  /** Whether this is a real atproto account (vs a guest did:key). */
  isGuest: boolean
  /** Mint a fresh service-auth JWT bound to aud=CALLS_SERVICE_DID, lxm=CALLS_LXM. */
  getToken: () => Promise<string>
  /** A display name to advertise in the room (handle for real users). */
  displayName?: string
}

function base64Url(input: string): string {
  return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Assembles + signs a compact service-auth JWT with a guest did:key identity.
 */
async function mintGuestToken(identity: CallIdentity): Promise<string> {
  const header = {typ: 'JWT', alg: identity.alg}
  const payload = {
    iss: identity.did,
    aud: CALLS_SERVICE_DID,
    lxm: CALLS_LXM,
    exp: Math.floor(Date.now() / 1000) + TOKEN_TTL_SECONDS,
  }
  const signingInput = `${base64Url(JSON.stringify(header))}.${base64Url(
    JSON.stringify(payload),
  )}`
  const sig = await identity.signJwtInput(signingInput)
  return `${signingInput}.${sig}`
}

/**
 * Resolves a CallSession for the current user.
 *
 * @param opts.agent - The atproto agent if the user has a real session; else
 *   undefined/null to fall back to a guest did:key.
 * @param opts.handle - Optional display handle for a real user.
 */
export async function resolveCallSession(opts: {
  agent?: AtpAgent | null
  hasSession?: boolean
  handle?: string
}): Promise<CallSession> {
  const {agent, hasSession, handle} = opts

  if (agent && hasSession && agent.did) {
    // Real atproto user: PDS mints the token.
    return {
      did: agent.did,
      isGuest: false,
      displayName: handle,
      getToken: () =>
        getServiceAuthToken({
          agent,
          aud: CALLS_SERVICE_DID,
          lxm: CALLS_LXM,
          exp: Math.floor(Date.now() / 1000) + TOKEN_TTL_SECONDS,
        }),
    }
  }

  // Guest: local did:key.
  const identity = await getGuestIdentity()
  return {
    did: identity.did,
    isGuest: true,
    getToken: () => mintGuestToken(identity),
  }
}
